import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  type ListParsingCasesParams,
  type PaginatedParsingCasesResponse,
} from './types';
import {
  zodListParsingCasesParamsSchema,
  zodPaginatedParsingCasesResponseSchema,
} from './validations';

/**
 * Fetches a paginated list of DNPv2 parsing cases across all companies (vadmin only).
 *
 * @param params - Filter and pagination parameters
 * @param options - Optional request options (e.g. abort signal)
 * @returns Paginated list of parsing cases
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/list_parsing_cases_vadmin_parsing_cases_get
 */
export const fetchParsingCases = async (
  params: ListParsingCasesParams,
  options?: KyRequestOptions,
) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params).filter(
      ([_, value]) => value !== undefined && value !== '',
    ),
  );

  const response = await vestigasApi
    .get(ENDPOINT.PARSING_CASE.GET_ALL(), {
      searchParams: filteredParams,
      validate: { searchParams: zodListParsingCasesParamsSchema },
      ...options,
    })
    .json<PaginatedParsingCasesResponse>();

  const validated = zodPaginatedParsingCasesResponseSchema.safeParse(response);

  if (!validated.success) {
    log.error({
      message: 'Parsing cases list API response has validation errors',
      error: {
        params: filteredParams,
        errors: validated.error.issues,
        errorMessage: validated.error.message,
      },
    });
    return response;
  }

  return validated.data;
};
